/**
 * Vitrine — Shared layout for all public-facing pages.
 *
 * Includes the nav, footer, tracking init, and adds top padding when the
 * current page is NOT the immersive landing (whose hero handles the nav).
 */
import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import VitrineNav from "./VitrineNav";
import VitrineFooter from "./VitrineFooter";
import MobileBookFab from "./MobileBookFab";
import SEOHead from "./SEOHead";
import NewsletterPopup from "./NewsletterPopup";
import FeaturedEventPopup from "./FeaturedEventPopup";
import { initTracking, trackPageView } from "../../lib/tracking";
import { ComingSoonProvider } from "../../context/ComingSoonContext";

// Routes whose hero already starts at y=0 with full-bleed image.
const TRANSPARENT_NAV_ROUTES = new Set([
  "/",
]);
const HERO_PREFIX = ["/univers/", "/le-kaai", "/hebergement/"];

export default function VitrineLayout() {
  const loc = useLocation();

  useEffect(() => { initTracking(); }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    trackPageView();
  }, [loc.pathname]);

  const heroPage =
    TRANSPARENT_NAV_ROUTES.has(loc.pathname) ||
    HERO_PREFIX.some((p) => loc.pathname.startsWith(p));
  // iter-74 — hide cross-sell modals while staff creates a booking on behalf
  // of a customer via /booking/...?source=staff
  const isStaffMode = new URLSearchParams(loc.search).get("source") === "staff";
  // iter-107 — also suppress promo modals while the customer is inside the
  // booking tunnel: the FeaturedEventPopup can re-mount 5 s after any
  // newsletter close and its full-screen overlay intercepts clicks on the
  // step-summary / payment buttons.
  const isBookingTunnel = loc.pathname.startsWith("/booking/") || loc.pathname.startsWith("/booking-extras/");
  const suppressPromos = isStaffMode || isBookingTunnel;

  return (
    <ComingSoonProvider>
      <div className="bg-[#FAF7F2] text-[#0A0A0A] min-h-screen flex flex-col"
           data-testid="vitrine-layout">
        <SEOHead />
        <VitrineNav />
        <main className={`flex-1 ${heroPage ? "" : "pt-24 md:pt-28 lg:pt-32"}`}>
          <Outlet />
        </main>
        <VitrineFooter />
        <MobileBookFab />
        {/* iter-66 — Global newsletter + featured event promo popups.
            iter-74 — suppressed when the tunnel is opened in ?source=staff mode
            so staff isn't nagged by cross-sell modals while creating a booking. */}
        {!suppressPromos && <NewsletterPopup />}
        {!suppressPromos && <FeaturedEventPopup />}
      </div>
    </ComingSoonProvider>
  );
}
